import { Box, Typography, Paper, Button, TextField, CircularProgress } from "@mui/material";
import LoginFooter from "./LoginFooter";
import { LoginHeader } from "./LoginHeader";
import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import loginImage from "../../assets/images/login-image.png";
import { postDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { useNavigate } from "react-router-dom";
import { RouterPathEnum } from "../../constants/router";

const UserRequestForm = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [requestReason, setRequestReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleSetContentHeight = () => {
    const headerHeight =
      document.getElementById("loginHeader")?.offsetHeight || 0;
    const footerHeight =
      document.getElementById("loginFooterContainer")?.offsetHeight || 0;
    const totalHeight = window.innerHeight - headerHeight - footerHeight;
    setContentHeight(totalHeight);
  };

  useEffect(() => {
    handleSetContentHeight();
    window.addEventListener("resize", handleSetContentHeight);
    return () => window.removeEventListener("resize", handleSetContentHeight);
  }, []);

  const handleSubmit = async () => {
    if (!requestReason.trim()) {
      setError("Please enter a reason for your request");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const url = `${RestfulUrls.BASE_URL}${RestfulUrls.SUBMIT_USER_REQUEST}`;
      const payloadData = { requestReason: requestReason.trim() };

      await postDataFromApi<any>(
        url,
        payloadData,
        () => {
          // Success - redirect to UserInactive page
          navigate(`/${RouterPathEnum.USER_INACTIVE}`);
        },
        { isAuthRequired: true },
        navigate,
        () => {
          setError("Failed to submit request. Please try again.");
          setIsSubmitting(false);
        }
      );
    } catch (err) {
      setError("An error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: "/login",
      });
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <Box
      id="loginMainContainer"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#f4f6f8",
      }}
    >
      <Box id="loginHeader">
        <LoginHeader />
      </Box>

      <Box
        sx={{
          height: contentHeight,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              component="img"
              src={loginImage}
              alt="Login"
              sx={{
                width: "120px",
                height: "auto",
                objectFit: "cover",
                alignSelf: "flex-start",
                mb: 2,
              }}
            />

            <Typography variant="h4" gutterBottom align="center">
              Request Access
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3 }} align="center">
            Your account is not registered. Please provide a reason for your access request.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason of the request"
              value={requestReason}
              onChange={(e) => {
                setRequestReason(e.target.value);
                setError("");
              }}
              error={!!error}
              helperText={error}
              placeholder="Please explain why you need access to this application..."
              variant="outlined"
            />
          </Box>

          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              onClick={handleLogout}
              variant="outlined"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting || !requestReason.trim()}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Box id="loginFooterContainer">
        <LoginFooter />
      </Box>
    </Box>
  );
};

export default UserRequestForm;

